import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardBody, CardHeader, Col, Container, Row, Label } from 'reactstrap';
import RevoGridTable from '../../Components/Common/RevoGridTable';
import Flatpickr from "react-flatpickr";
import useOrdenesTrabajo from '../../Components/Hooks/useOrdenesTrabajo';
import ModalDetalleOT from '../../Components/modals/ModalDetalleOT';


const OrdenesTrabajo = () => {
    // Estados para fechas
    const d = new Date();
    let hoy = d.toISOString().substring(0, 10);
    const [fechaDesde, setFechaDesde] = useState(hoy);
    const [fechaHasta, setFechaHasta] = useState(hoy);

    // Estado para modal
    const [ordenSeleccionada, setOrdenSeleccionada] = useState(null);
    const [modalDetalleOpen, setModalDetalleOpen] = useState(false);

    // Hook para obtener órdenes de trabajo
    const { ordenes, loading, error } = useOrdenesTrabajo(fechaDesde, fechaHasta);

    // Formatear fecha para input
    const formatDate = (date) => {
        if (!date) return '';
        const _date = new Date(date);
        const year = _date.getFullYear();
        const month = String(_date.getMonth() + 1).padStart(2, '0');
        const day = String(_date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Handlers para fechas
    const handleFechaDesdeChange = (date) => {
        setFechaDesde(formatDate(date[0]));
    };

    const handleFechaHastaChange = (date) => {
        setFechaHasta(formatDate(date[0]));
    };

    // Handler para clic en celda
    const handleCellClick = (cellInfo) => {
        if (cellInfo?.rowData) {
            setOrdenSeleccionada(cellInfo.rowData);
            setModalDetalleOpen(true);
        }
    };

    // Toggle modal
    const toggleModalDetalle = () => {
        setModalDetalleOpen(!modalDetalleOpen);
        if (modalDetalleOpen) {
            setOrdenSeleccionada(null);
        }
    };

    // Configurar título de página
    useEffect(() => {
        document.title = "Órdenes de Trabajo | Siga ERP";
    }, []);

    // Definir columnas de la tabla para RevoGrid
    const columns = useMemo(() => [
        {
            name: "N° CITA",
            prop: "NUMCTR",
            minSize: 90,
            //sortable: true,
            cellTemplate: (h, props) => h('span', {}, props.model.NUMCTR || '-'),
        },
        {
            name: "N° OT",
            prop: "NUMEOT",
            minSize: 90,
            //sortable: true,
            cellTemplate: (h, props) => h('span', {}, props.model.NUMEOT || '-'),
        },
        {
            name: "HORA CITA",
            prop: "fecha_cita",
            minSize: 95,
            sortable: false,
            cellTemplate: (h, props) => {
                const value = props.model.fecha_cita;
                if (!value) return h('span', {}, '--:--');
                const formatted = typeof value === 'string' && value.includes(':')
                    ? value.includes('T') ? value.split('T')[1].substring(0, 5) : value.substring(0, 5)
                    : value;
                return h('span', {}, formatted);
            },
        },
        {
            name: "FECHA OT",
            prop: "FECOTR",
            minSize: 95,
            sortable: false,
            cellTemplate: (h, props) => {
                const value = props.model.FECOTR;
                if (!value) return h('span', {}, '--:--');
                const formatted = typeof value === 'string' && value.includes('T')
                    ? value.split('T')[0] : value;
                return h('span', {}, formatted);
            },
        },
        {
            name: "HORA ING.OT",
            prop: "FECINI",
            minSize: 105,
            //sortable: true,
            cellTemplate: (h, props) => {
                const value = props.model.FECINI;
                if (!value) return h('span', {}, '--:--');
                const formatted = typeof value === 'string' && value.includes(':')
                    ? value.includes('T') ? value.split('T')[1].substring(0, 5) : value.substring(0, 5)
                    : value;
                return h('span', {}, formatted);
            },
        },
        {
            name: "TIPO DE OT",
            prop: "tipo_ot",
            minSize: 110,
            //sortable: true,
            cellTemplate: (h, props) => h('span', {}, props.model.tipo_ot || '-'),
        },
        {
            name: "PLACA PATENTE",
            prop: "PATENT",
            minSize: 120,
            //sortable: true,
            cellTemplate: (h, props) => h('span', {}, props.model.PATENT || '-'),
        },
        {
            name: "MARCA",
            prop: "descri_marca",
            minSize: 140,
            //sortable: true,
            cellTemplate: (h, props) => h('span', {}, props.model.descri_marca),
        },
        {
            name: "MODELO",
            prop: "descri_modelo",
            minSize: 140,
            //sortable: true,
            cellTemplate: (h, props) => h('span', {}, props.model.descri_modelo),
        },
        {
            name: "AÑO",
            prop: "ANOFAB",
            minSize: 70,
            //sortable: true,
            cellTemplate: (h, props) => h('span', {}, props.model.ANOFAB || '-'),
        },
        {
            name: "KM",
            prop: "LECTUR",
            minSize: 90,
            //sortable: true,
            cellTemplate: (h, props) => {
                const value = props.model.LECTUR;
                return h('span', {}, value ? value.toLocaleString('es-CL') : '-');
            },
        },
        {
            name: "N° MOTOR",
            prop: "NMOTOR",
            minSize: 140,
            //sortable: true,
            cellTemplate: (h, props) => h('span', {}, props.model.NMOTOR || '-'),
        },
        {
            name: "CHASIS",
            prop: "NCHASI",
            minSize: 140,
            //sortable: true,
            cellTemplate: (h, props) => h('span', {}, props.model.NCHASI || '-'),
        },
        {
            name: "SOLICITUD DEL CLIENTE",
            prop: "OBSERV",
            minSize: 220,
            //sortable: true,
            cellTemplate: (h, props) => {
                const value = props.model.OBSERV || '';
                return h('span', { title: value }, value || '-');
            },
        },
        {
            name: "RUT CLIENTE",
            prop: "CLIENT",
            minSize: 110,
            //sortable: true,
            cellTemplate: (h, props) => h('span', {}, props.model.CLIENT || '-'),
        },
        {
            name: "NOMBRE / RAZÓN SOCIAL",
            prop: "RAZSOC",
            minSize: 180,
            //sortable: true,
            cellTemplate: (h, props) => h('span', {}, props.model.RAZSOC || '-'),
        },
        {
            name: "CONTACTO",
            prop: "CONDUC",
            minSize: 140,
            //sortable: true,
            cellTemplate: (h, props) => h('span', {}, props.model.CONDUC || '-'),
        },
        {
            name: "TEL. CONTACTO",
            prop: "FONCON",
            minSize: 120,
            //sortable: true,
            cellTemplate: (h, props) => h('span', {}, props.model.FONCON || '-'),
        },
        {
            name: "E-MAIL CLIENTE",
            prop: "CEMAIL",
            minSize: 170,
            //sortable: true,
            cellTemplate: (h, props) => h('span', {}, props.model.CEMAIL || '-'),
        },
    ], []);

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardHeader className="d-flex justify-content-between align-items-center">
                                    <h5 className="card-title mb-0">Órdenes de Trabajo</h5>
                                    <div className="d-flex align-items-center">
                                        <Label className="form-label mb-0 me-2">Desde</Label>
                                        <div className="mt-0 me-2">
                                            <Flatpickr
                                                className="form-control"
                                                options={{
                                                    mode: "single",
                                                    dateFormat: "Y-m-d"
                                                }}
                                                value={fechaDesde}
                                                onChange={handleFechaDesdeChange}
                                            />
                                        </div>
                                        <Label className="form-label mb-0 me-2">Hasta</Label>
                                        <div className="mt-0">
                                            <Flatpickr
                                                className="form-control"
                                                options={{
                                                    mode: "single",
                                                    dateFormat: "Y-m-d"
                                                }}
                                                value={fechaHasta}
                                                onChange={handleFechaHastaChange}
                                            />
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardBody>
                                    {error ? (
                                        <div className="alert alert-danger">
                                            <i className="ri-error-warning-line me-2"></i>
                                            {error}
                                        </div>
                                    ) : (
                                        <>
                                            <RevoGridTable
                                                columns={columns}
                                                data={ordenes}
                                                loading={loading}
                                                enableSelection={true}
                                                onRowClick={handleCellClick}
                                                isGlobalFilter={true}
                                                SearchPlaceholder='Buscar...'
                                                theme="excel"
                                                resize={true}
                                                exportFilename="ordenes_trabajo"
                                                height="550px"
                                            />
                                        </>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>

            <ModalDetalleOT
                isOpen={modalDetalleOpen}
                toggle={toggleModalDetalle}
                ordenTrabajo={ordenSeleccionada}
            />
        </React.Fragment>
    );
};

export default OrdenesTrabajo;
